import { useEffect } from 'react';

// Leaflet measures its container once, at mount. Any container whose final size
// settles *after* that — a page whose header/filter bar is still laying out, an
// expanding card body — leaves the tile layer on the stale viewport: blank tiles
// below and to the right, with markers floating in the white space.
//
// Fix in two parts:
//   1. A post-mount nudge. One requestAnimationFrame gets past React's commit;
//      a second 0ms timeout catches parent flex/grid layouts still settling on
//      the same tick.
//   2. A ResizeObserver on the container, so later resizes (window resize,
//      content above the map expanding, a card unfolding) reflow the tiles too.
//      Browsers without ResizeObserver fall back to the window resize event.
//
// `deps` re-runs the nudge for anything that changes the map's box or contents
// (e.g. new bounds). The observer covers steady-state resizes on its own, so an
// empty `deps` is the common case.
//
// @param {object} mapRef  ref to a react-leaflet <Map> (reads `leafletElement`)
// @param {Array} [deps]   extra triggers for the post-mount nudge
export default function useLeafletAutoInvalidate(mapRef, deps = []) {
  useEffect(() => {
    const mapEl =
      mapRef.current && mapRef.current.leafletElement
        ? mapRef.current.leafletElement
        : null;
    if (!mapEl) return undefined;

    // Guarded: an unmount between scheduling and firing leaves Leaflet without
    // the internals invalidateSize touches.
    const invalidate = () => {
      try {
        mapEl.invalidateSize(false);
      } catch {
        /* unmounted */
      }
    };

    const rafId = requestAnimationFrame(invalidate);
    const timerId = setTimeout(invalidate, 0);

    const container = mapEl.getContainer ? mapEl.getContainer() : null;
    let observer = null;
    let onResize = null;
    if (container && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(invalidate);
      observer.observe(container);
    } else {
      onResize = invalidate;
      window.addEventListener('resize', onResize);
    }

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timerId);
      if (observer) observer.disconnect();
      if (onResize) window.removeEventListener('resize', onResize);
    };
    // `deps` is the caller's pass-through trigger list, so the rule can't
    // statically verify it — the standard exception for a custom hook that
    // forwards a deps array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
